import { cloneDeep, findIndex, get, isEmpty, orderBy } from 'lodash';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { closeGenericModal } from '@actions/modal';
import { loading, loadingFailure, loadingSuccess } from '@actions/loading';
import {
  showConfirmationMessage,
  showErrorMessage,
  showSuccessMessage,
} from '@actions/messageconfirmation';

import { ENUM_MODULE_NAME, isUserAllowedToAccessProduction } from '@commons/utils/features';
import { getChannelsTranslations } from '@commons/utils/translations';
import { getIsCentralMode } from '@commons/utils/localStorage';
import { isUserManager } from '@commons/utils/roles';
import AuditHistoryDrawer from '@commons/HistoryDrawer/AuditHistoryDrawer';
import NavBar from '@commons/NavigationBar';

import { getClientInfo } from '@selectors/client';
import { getHasRecipeVariants } from '@selectors/featureProps';

import useAuditHistory from '@hooks/useAuditHistory';

import { product as productService } from '@services/product';
import channelService from '@services/channel';
import recipeService from '@services/recipe';

import Performances from '@admin/products/components/Performances';

import EnableProductModal, {
  enableProductMapCashierProductsAndAddProductionParameters,
} from '../components/EnableProductModal';

import {
  Container,
  ContainerContent,
  NavigationBar,
  NavigationPage,
  Section,
} from './styledComponents';
import { isForbiddenAPIError } from '@commons/utils/errors';
import CashierProductAssociation from './components/CashierProductAssociation';
import ProductComposition, { getCompositionTypes } from './components/ProductRecipeAssociation';
import ProductDetailsBottomBar from './components/BottomBar';
import ProductInfoSection from './components/ProductInfo';
import ProductPictureSection from './components/ProductPicture';
import ProductProductionPlanningAssociation from './components/ProductProductionPlanningAssociation';
import SimpleModifierCashierProductsList from './components/SimpleModifierCashierProductsList';
import utilsProductInfo from './components/ProductInfo/utils';

const PRODUCT_ID_CREATE_MODE = 'create';
const EMPTY_PRODUCT = { name: '', active: true };

const COMPOSITION_TYPE = {
  NONE: 'NONE',
  RECIPE: 'RECIPE',
  INGREDIENT: 'INGREDIENT',
};

const buildIngredientComposition = (composition, channels) => {
  if (isEmpty(composition)) {
    return [];
  }

  return [
    {
      entity: composition,
      quantity: 1,
      channelIds: channels.map(({ id }) => id),
    },
  ];
};

const getRecipeSubPage = () => ({
  name: i18next.t('ADMIN.PRODUCTS.COMPOSITION'),
  render: (props, isReadOnly) => (
    <ProductComposition
      channels={props.channels}
      clientChannels={props.clientChannels}
      clientHasMultipleChannels={props.clientHasMultipleChannels}
      composition={props.composition}
      hasRecipeVariant={props.hasRecipeVariant}
      isReadOnly={isReadOnly}
      product={props.product}
      selectedCompoType={props.selectedCompoType}
      setComposition={props.setComposition}
      setRecipeEatInside={props.setRecipeEatInside}
      setRecipeTakeAway={props.setRecipeTakeAway}
      setSelectedCompoType={props.setSelectedCompoType}
      onProductChange={props.onProductChange}
    />
  ),
});

const getPerformancesSubPage = () => ({
  name: i18next.t('ADMIN.RECIPES.PERFORMANCES'),
  render: (props) => (
    <Performances
      associatedProduct={props.product}
      clientChannels={props.clientChannelsWithTranslations}
      compositions={props.performancesCompositions}
      currency={props.currency}
      defaultChannelId={props.defaultChannelId}
      emptyStateLabel={props.performancesEmptyStateLabel}
      isEmptyState={props.isPerformancesEmptyState}
      recipe={props.performancesRecipe}
      recipeVariants={props.performancesRecipeVariants}
      stores={props.stores}
    />
  ),
});

const getCashierProductsSubPage = (cashierProducts) => ({
  name: i18next.t('ADMIN.PRODUCTS.CASHIER_PRODUCTS_SECTION_TITLE', {
    count: cashierProducts.length,
  }),
  render: (props, isReadOnly) => (
    <CashierProductAssociation
      cashierProducts={props.cashierProducts}
      fetchAuditHistory={props.fetchAuditHistory}
      history={props.history}
      isReadOnly={isReadOnly}
      product={props.product}
      selectedCashierProduct={props.selectedCashierProduct}
      setCashierProducts={props.setCashierProducts}
      setSelectedCashierProduct={props.setSelectedCashierProduct}
      setSimpleModifierCashierProducts={props.setSimpleModifierCashierProducts}
    />
  ),
});

const getSimpleModifierCashierProductsSubPage = (
  simpleModifierCashierProducts,
  isProductChild,
) => ({
  name: i18next.t(
    isProductChild
      ? 'ADMIN.PRODUCTS.SIMPLE_MODIFIER_CASHIER_PRODUCTS_FOR_CHILD_PRODUCT'
      : 'ADMIN.PRODUCTS.SIMPLE_MODIFIER_CASHIER_PRODUCTS_FOR_PARENT_PRODUCT',
    { count: simpleModifierCashierProducts.length },
  ),
  render: () => (
    <SimpleModifierCashierProductsList
      isProductChild={isProductChild}
      simpleModifierCashierProducts={simpleModifierCashierProducts}
    />
  ),
});

const getProductionPlanningSubPage = (storesProductionPlanning) => ({
  name: i18next.t(
    !getIsCentralMode()
      ? 'ADMIN.SUB_FEATURES.PRODUCTION_ASSORTMENTS'
      : 'ADMIN.PRODUCTS.PRODUCTION_PLANNING',
    { count: storesProductionPlanning.length },
  ),
  render: (props, isReadOnly) => (
    <ProductProductionPlanningAssociation
      history={props.history}
      isReadOnly={isReadOnly}
      product={props.product}
      setStoresProductionPlanning={props.setStoresProductionPlanning}
      storesProductionPlanning={props.storesProductionPlanning}
    />
  ),
});

export function renderSectionProductInfo(props, isReadOnly) {
  return (
    <Section>
      <ProductInfoSection
        isProductSynced={props.isProductSynced}
        isReadOnly={isReadOnly}
        product={props.product}
        productId={props.productId}
        productsCategories={props.productsCategories}
        productsSubCategories={props.productsSubCategories}
        setProductsCategories={props.setProductsCategories}
        setProductsSubCategories={props.setProductsSubCategories}
        onProductChange={props.onProductChange}
      />

      <ProductPictureSection
        isReadOnly={isReadOnly}
        product={props.product}
        onProductChange={props.onProductChange}
      />
    </Section>
  );
}

export function renderSectionProductionSubPages(props, isReadOnly) {
  const { activeSubPages, product } = props;

  return (
    <>
      {activeSubPages.length > 1 && (
        <Section>
          <NavigationBar>
            {activeSubPages.map((page, indexPage) => (
              <NavigationPage
                className={props.selectedProductSubPage.name === page.name ? 'selected' : ''}
                isDisabled={!product.active}
                key={indexPage}
                onClick={() => product.active && props.onSelectedProductSubPage(page)}
              >
                {page.name}
              </NavigationPage>
            ))}
          </NavigationBar>
        </Section>
      )}

      <Section nbSubPages={props.activeSubPages.length} sectionType="subpage">
        {props.selectedProductSubPage.render(props, isReadOnly)}
      </Section>
    </>
  );
}

export function renderContent(props) {
  const path = get(props, 'match.path');

  const isManager = isUserManager(props.user);

  const isReadOnly = isManager || !get(props.product, 'active');

  return (
    <>
      <NavBar
        action={
          !isManager && (
            <ProductDetailsBottomBar
              auditButtonLabel={props.auditButtonLabel}
              auditButtonLoading={props.auditButtonLoading}
              cashierProducts={props.cashierProducts}
              channels={props.channels}
              clientHasMultipleChannels={props.clientHasMultipleChannels}
              composition={props.composition}
              history={props.history}
              pageLoaded={props.pageLoaded}
              pageLoading={props.pageLoading}
              product={props.product}
              productId={props.productId}
              productsCategories={props.productsCategories}
              productsSubCategories={props.productsSubCategories}
              selectedCompoType={props.selectedCompoType}
              setOpenEnableModal={props.setOpenEnableModal}
              showMessage={props.showMessage}
              user={props.user}
              onOpenAuditDrawer={props.onOpenAuditDrawer}
            />
          )
        }
        bigTopBar={true}
        isProductChild={props.isProductChild}
        module={ENUM_MODULE_NAME.PRODUCT_DETAIL}
        path={path}
        product={props.product}
        productId={props.productId}
      />
      <ContainerContent>
        {renderSectionProductInfo(props, isReadOnly)}
        {renderSectionProductionSubPages(props, isReadOnly)}
      </ContainerContent>
    </>
  );
}

/**
 * Retrieve the list of deepsight channels and set it to the local state
 *
 * @param {Function} setChannels - Method to set the local state channels
 *
 * @returns {Promise<void>}
 */
export async function fetchChannels(setChannels) {
  const channels = await channelService.getChannels();

  setChannels(channels);

  return channels;
}

/**
 * Retrieve the list of stores mapped with this product
 *
 * @param {String} productId                      - The id of the product on which retrieve the mapped stores
 * @param {Function} setStoresProductionPlanning  - Method to set the local state storesProductionPlanning
 *
 * @returns {Promise<void>}
 */
export async function fetchMappedStores(productId, setStoresProductionPlanning) {
  const storesProductionPlanning = await productService.getProductionStores(productId);

  const activeStoresProductionPlanning = storesProductionPlanning.filter(({ active }) => active);

  setStoresProductionPlanning(orderBy(activeStoresProductionPlanning, 'storeName'));

  return activeStoresProductionPlanning;
}

/**
 * Retrieve the list of cashier products mapped with this product
 *
 * @param {String} productId             - The id of the product on which retrieve the mapped cashier products
 * @param {Function} setCashierProducts  - Method to set the local state cashierProducts
 *
 * @returns {Promise<void>}
 */
export async function fetchCashierProducts(
  productId,
  setCashierProducts,
  setSimpleModifierCashierProducts,
) {
  const { cashierProducts, simpleModifierCashierProducts } =
    await productService.getCashierProductWithSimpleModifierByProductId(productId);

  setCashierProducts(orderBy(cashierProducts, 'name'));

  const formattedSimpleModifierCashierProducts = simpleModifierCashierProducts.map(
    (cashierProduct) => ({
      ...cashierProduct,
      productName: cashierProduct.associatedProduct?.name ?? '',
    }),
  );

  setSimpleModifierCashierProducts(orderBy(formattedSimpleModifierCashierProducts, 'name'));

  return {
    fetchedCashierProducts: cashierProducts,
    fetchedSimpleModifierCashierProducts: formattedSimpleModifierCashierProducts,
  };
}

export const ProductDetails = (props) => {
  const {
    history,
    match: { params },
    client: {
      hasMultipleChannels,
      areProductsSync,
      hasSimpleModifierManagement,
      clientChannels,
      defaultChannelId,
    },
    currency,
    hasUserAccessToProductionFeature,
    hasVariants,
    stores,
    showErrorMessage,
  } = props;

  const canAccessProductionPlanning = hasUserAccessToProductionFeature;

  const compositionTypes = getCompositionTypes();

  const clientChannelsWithTranslations = getChannelsTranslations(clientChannels || []);

  const [productId, setProductId] = useState(params.id);

  const [cashierProducts, setCashierProducts] = useState([]);
  const cashierProductsRef = useRef();

  const [simpleModifierCashierProducts, setSimpleModifierCashierProducts] = useState([]);

  const [storesProductionPlanning, setStoresProductionPlanning] = useState([]);
  const storesProductionPlanningRef = useRef();

  const [product, onProductChange] = useState(EMPTY_PRODUCT);

  const {
    auditLogs,
    auditButtonLabel,
    auditButtonLoading,
    isAuditDrawerOpened,
    fetchAuditHistory,
    toggleAuditDrawer,
    closeAuditDrawer,
  } = useAuditHistory({ showErrorMessage, targetId: product?.id });

  const [isLoading, setLoading] = useState(true);
  const [isProductChild, setIsProductChild] = useState(false);

  const [activeSubPages, setActiveSubPages] = useState([
    getRecipeSubPage(),
    getPerformancesSubPage(),
  ]);

  const [selectedProductSubPage, onSelectedProductSubPage] = useState();

  const [selectedCashierProduct, setSelectedCashierProduct] = useState({});
  const [isProductSynced, setIsProductSynced] = useState(true);

  const [openEnableModal, setOpenEnableModal] = useState(false);

  const [productsCategories, setProductsCategories] = useState([]);
  const [productsSubCategories, setProductsSubCategories] = useState([]);

  // Product composition
  const [composition, setComposition] = useState({});
  const [selectedCompoType, setSelectedCompoType] = useState(compositionTypes[0]);

  const [associatedRecipe, setAssociatedRecipe] = useState(null);
  const [associatedRecipeCompositions, setAssociatedRecipeCompositions] = useState([]);
  const [associatedRecipeVariants, setAssociatedRecipeVariants] = useState([]);
  const [isPerformancesLoading, setIsPerformancesLoading] = useState(false);

  const selectedCompoTypeId = selectedCompoType?.id;

  /**
   * Takes care of the loading state and success/failure toaster for the API call
   * @param {*} formattedData Result from formatDataForApiCall in EnableProductModal/modalConfiguration.js
   */
  const handleCallToEnableProduct = async (formattedData) => {
    props.pageLoading();

    try {
      await enableProductMapCashierProductsAndAddProductionParameters(formattedData);

      setProductId(null); // force useEffect for refresh
      setProductId(productId);
      props.showSuccessMessage(i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCT_SUCCESS'));
    } catch (err) {
      props.showErrorMessage(
        i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCT_FAILURE', {
          step: err.message,
        }),
      );
    } finally {
      props.pageLoaded();
    }
  };

  const setCompositionOnFirstLoad = (fetchedProduct) => {
    if (!fetchedProduct.lnkEntityProductrel) {
      return;
    }

    setComposition(fetchedProduct.lnkEntityProductrel);
    setSelectedCompoType(
      fetchedProduct.lnkEntityProductrel.isIngredient ? compositionTypes[2] : compositionTypes[1],
    );
  };

  useEffect(() => {
    props.pageLoading();

    onSelectedProductSubPage(getRecipeSubPage());

    (async function loadData() {
      const subPagesToAdd = [];

      try {
        if (!productId || productId === PRODUCT_ID_CREATE_MODE) {
          setLoading(false);

          props.pageLoaded();

          return;
        }

        const fetchedProduct = await productService.getById(productId);

        if (!!fetchedProduct.lnkEntityProductrel) {
          const recipeCost = await recipeService.getRecipeCost(fetchedProduct.entityId, {
            withAllergens: false,
          });

          fetchedProduct.lnkEntityProductrel.costByChannelId = recipeCost.costByChannelId;
        }

        const priceHTByChannelId = {};

        (clientChannels || []).forEach((channel) => {
          priceHTByChannelId[channel.id] = utilsProductInfo.getProductPriceExcludingVAT(
            fetchedProduct,
            channel.id,
          );
        });

        onProductChange({
          ...fetchedProduct,
          brand: fetchedProduct.lnkBrandProductrel ? fetchedProduct.lnkBrandProductrel.name : '',
          price: fetchedProduct.priceWithTaxes,
          deliveryPrice: fetchedProduct.deliveryPriceWithTaxes,
          priceHTByChannelId,
        });

        setCompositionOnFirstLoad(fetchedProduct);

        fetchAuditHistory(productId);

        props.pageLoaded();

        setLoading(false);

        if (areProductsSync) {
          const { fetchedCashierProducts, fetchedSimpleModifierCashierProducts } =
            await fetchCashierProducts(
              productId,
              setCashierProducts,
              setSimpleModifierCashierProducts,
            );

          const isChildProduct = fetchedCashierProducts.some(
            ({ parentCashierProduct }) => !!parentCashierProduct,
          );

          setIsProductChild(isChildProduct);

          if (
            !activeSubPages.find(
              (page) => page.name === getCashierProductsSubPage(fetchedCashierProducts).name,
            )
          ) {
            subPagesToAdd.push(getCashierProductsSubPage(fetchedCashierProducts));
          }

          if (
            hasSimpleModifierManagement &&
            fetchedSimpleModifierCashierProducts.length > 0 &&
            !activeSubPages.find(
              (page) =>
                page.name ===
                getSimpleModifierCashierProductsSubPage(
                  fetchedSimpleModifierCashierProducts,
                  isChildProduct,
                ).name,
            )
          ) {
            subPagesToAdd.push(
              getSimpleModifierCashierProductsSubPage(
                fetchedSimpleModifierCashierProducts,
                isChildProduct,
              ),
            );
          }
        }

        if (canAccessProductionPlanning) {
          const fetchedStores = await fetchMappedStores(productId, setStoresProductionPlanning);
          if (
            !activeSubPages.find(
              (page) => page.name === getProductionPlanningSubPage(fetchedStores).name,
            )
          ) {
            subPagesToAdd.push(getProductionPlanningSubPage(fetchedStores));
          }
        }
      } catch (err) {
        props.pageDidNotLoad();
        setLoading(false);

        if (isForbiddenAPIError(err)) {
          history.goBack();
        }
      } finally {
        setActiveSubPages(activeSubPages.concat(subPagesToAdd));
      }
    })();
  }, [productId]);

  // Load the associated recipe (and its variants) for the Performances tab. Only recipe
  // compositions need a fetch; ingredient compositions are derived synchronously below.
  useEffect(() => {
    if (selectedCompoTypeId !== COMPOSITION_TYPE.RECIPE || !product.entityId) {
      setAssociatedRecipe(null);
      setAssociatedRecipeCompositions([]);
      setAssociatedRecipeVariants([]);
      setIsPerformancesLoading(false);
      return undefined;
    }

    let isCancelled = false;

    (async function loadAssociatedRecipe() {
      setIsPerformancesLoading(true);

      try {
        const [{ recipe: fetchedRecipe }, fetchedCompositions, { recipeVariants }] =
          await Promise.all([
            recipeService.getRecipe(product.entityId, false),
            recipeService.getCompositions(product.entityId),
            hasVariants
              ? recipeService.getRecipeVariants(product.entityId)
              : Promise.resolve({ recipeVariants: [] }),
          ]);

        if (isCancelled) {
          return;
        }

        setAssociatedRecipe(fetchedRecipe || null);
        setAssociatedRecipeCompositions(fetchedCompositions);
        setAssociatedRecipeVariants(recipeVariants || []);
      } catch (err) {
        if (!isCancelled) {
          setAssociatedRecipe(null);
          setAssociatedRecipeCompositions([]);
          setAssociatedRecipeVariants([]);
          props.showErrorMessage(i18next.t('ADMIN.RECIPES.LOAD_DETAIL_FAILED'));
        }
      } finally {
        if (!isCancelled) {
          setIsPerformancesLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [product.entityId, selectedCompoTypeId, hasVariants]);

  useEffect(() => {
    const syncedCashierProduct = cashierProducts.find((cashierProduct) => cashierProduct.priceSync);

    if (syncedCashierProduct && syncedCashierProduct.id !== selectedCashierProduct.id) {
      setIsProductSynced(true);
      setSelectedCashierProduct(syncedCashierProduct);
    }

    if (!cashierProductsRef) {
      return;
    }

    if (cashierProductsRef.current) {
      const updatedActiveSubPages = cloneDeep(activeSubPages);

      const cashierProductActiveSubPageIndex = findIndex(
        updatedActiveSubPages,
        (page) => page.name === getCashierProductsSubPage(cashierProductsRef.current).name,
      );

      if (cashierProductActiveSubPageIndex >= 0) {
        updatedActiveSubPages[cashierProductActiveSubPageIndex] =
          getCashierProductsSubPage(cashierProducts);

        setActiveSubPages(updatedActiveSubPages);
      }
    }

    cashierProductsRef.current = cashierProducts;
  }, [cashierProducts]);

  // To update the value in parentheses in the production planning sub page title
  useEffect(() => {
    if (!storesProductionPlanningRef) {
      return;
    }

    if (storesProductionPlanningRef.current) {
      const updatedActiveSubPages = cloneDeep(activeSubPages);

      // spp stands for storesProductionPlanning
      const sppActiveSubPageIndex = findIndex(
        updatedActiveSubPages,
        (page) =>
          page.name === getProductionPlanningSubPage(storesProductionPlanningRef.current).name,
      );

      if (sppActiveSubPageIndex >= 0) {
        updatedActiveSubPages[sppActiveSubPageIndex] =
          getProductionPlanningSubPage(storesProductionPlanning);

        setActiveSubPages(updatedActiveSubPages);
      }
    }

    storesProductionPlanningRef.current = storesProductionPlanning;
  }, [storesProductionPlanning]);

  // To update the value in parentheses in the cashier products sub page title
  useEffect(() => {
    if (isEmpty(selectedCashierProduct)) {
      setIsProductSynced(false);

      const CPWithoutPriceSync = cashierProducts.map((cashierProduct) => ({
        ...cashierProduct,
        priceSync: false,
      }));

      setCashierProducts(CPWithoutPriceSync);
      return;
    }

    setIsProductSynced(true);

    const updatedChannelPrices = {
      ...product.channelPrices,
      [defaultChannelId]: {
        channelId: defaultChannelId,
        priceWithTaxes: selectedCashierProduct.priceWithTaxes,
        vatRate: selectedCashierProduct.vatRate || 0,
      },
    };

    const syncedProduct = { ...product, channelPrices: updatedChannelPrices };

    const priceHT = utilsProductInfo.getProductPriceExcludingVAT(syncedProduct, defaultChannelId);

    onProductChange({
      ...syncedProduct,
      vatRate: selectedCashierProduct.vatRate || 0,
      price: selectedCashierProduct.priceWithTaxes,
      priceHTByChannelId: {
        ...product.priceHTByChannelId,
        [defaultChannelId]: priceHT,
      },
    });

    const updateCashierProducts = cashierProducts.map((cashierProduct) => ({
      ...cashierProduct,
      priceSync: cashierProduct.id === selectedCashierProduct.id,
    }));

    setCashierProducts(updateCashierProducts);
  }, [selectedCashierProduct]);

  const isIngredientComposition = selectedCompoTypeId === COMPOSITION_TYPE.INGREDIENT;

  const ingredientComposition = useMemo(() => {
    if (!isIngredientComposition) {
      return [];
    }

    return buildIngredientComposition(composition, clientChannelsWithTranslations);
  }, [isIngredientComposition, composition, clientChannelsWithTranslations]);

  const performancesRecipe = isIngredientComposition
    ? { name: product.name, hasMultipleChannels: false }
    : associatedRecipe;

  const performancesCompositions = isIngredientComposition
    ? ingredientComposition
    : associatedRecipeCompositions;

  const performancesRecipeVariants = isIngredientComposition ? [] : associatedRecipeVariants;

  const hasRecipeVariant =
    !isIngredientComposition && hasVariants && associatedRecipeVariants.length > 0;

  const hasComposition =
    selectedCompoTypeId === COMPOSITION_TYPE.RECIPE ||
    selectedCompoTypeId === COMPOSITION_TYPE.INGREDIENT;
  const isPerformancesEmptyState = isPerformancesLoading || !hasComposition;
  const performancesEmptyStateLabel = isPerformancesLoading
    ? i18next.t('GENERAL.LOADING')
    : i18next.t('ADMIN.PRODUCTS.PERFORMANCES_NO_COMPOSITION_EMPTY_STATE');

  if (isLoading) {
    return null;
  }

  return (
    <Container>
      {renderContent({
        ...props,
        isProductChild,
        product,
        productId,
        onProductChange,
        clientChannels: clientChannels || [],
        clientChannelsWithTranslations,
        clientHasMultipleChannels: hasMultipleChannels,
        currency,
        stores,
        defaultChannelId,
        performancesRecipe,
        performancesCompositions,
        performancesRecipeVariants,
        hasRecipeVariant,
        isPerformancesEmptyState,
        performancesEmptyStateLabel,
        canAccessProductionPlanning,
        selectedProductSubPage,
        onSelectedProductSubPage,
        storesProductionPlanning,
        setStoresProductionPlanning,
        cashierProducts,
        setCashierProducts,
        selectedCashierProduct,
        setSelectedCashierProduct,
        activeSubPages,
        isProductSynced,
        setOpenEnableModal,
        composition,
        setComposition,
        selectedCompoType,
        setSelectedCompoType,
        productsCategories,
        setProductsCategories,
        productsSubCategories,
        simpleModifierCashierProducts,
        setProductsSubCategories,
        setSimpleModifierCashierProducts,
        auditButtonLabel,
        auditButtonLoading,
        fetchAuditHistory,
        onOpenAuditDrawer: toggleAuditDrawer,
      })}
      <AuditHistoryDrawer
        auditLogs={auditLogs}
        isOpened={isAuditDrawerOpened}
        subtitle={product.name}
        onClose={closeAuditDrawer}
      />
      {openEnableModal && (
        <EnableProductModal
          handleCallToEnableProduct={handleCallToEnableProduct}
          product={product}
          resetEnablingProduct={() => setOpenEnableModal(false)}
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  hasUserAccessToProductionFeature: isUserAllowedToAccessProduction(state.baseReducer.userRights),
  client: getClientInfo(state.baseReducer.user),
  stores: state.baseReducer.activeStores,
  currency: state.baseReducer.currency,
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  pageDidNotLoad: () => {
    dispatch(loadingFailure());
  },
  closeGenericModal: (params) => {
    dispatch(closeGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetails);
